import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FtpfileImportService } from 'src/app/services/ftpfile-import.service';

@Component({
    selector: 'app-import-log',
    templateUrl: './import-log.component.html',
    styleUrls: ['./import-log.component.scss'],
    standalone: false // NOSONAR
})
export class ImportLogComponent implements OnInit, AfterViewInit {
  @ViewChild('logsContainer') logsContainer?: ElementRef<HTMLDivElement>;

  successMessage: string | undefined;
  errorMessage: string | undefined;
  displaydata: Array<{ message: string }> = [];
  errormsg: string | undefined;
  params:any;
  templateFile: any;
  value: any;
  preloadedLogs: string[] = [];
  logFileNames: string[] = [];
  private readonly maxPollAttempts = 80;
  private readonly retryDelayMs = 1500;
  private pollAttempt = 0;
  private logFileNameCursor = 0;
  private isClosed = false;
  private hasTerminalState = false;
  private pollTimeoutRef: any;
  private readonly lineSet = new Set<string>();
  private readonly terminalMarkers = [
    'events validation failed',
    'validation failed',
    'internal server error',
    'backend error',
    'file upload / validation failed',
    'failed to upload file',
    'validated successfully',
    'were validated',
    'uploaded successfully',
    'were successfully uploaded',
    'task_complete',
    'task complete'
  ];



  constructor(public importService: FtpfileImportService, @Inject(MAT_DIALOG_DATA) public data: any,private readonly dialogRef:MatDialogRef<ImportLogComponent> ) {
       this.templateFile = data.fileSend
       this.value = data.valueSend
       this.preloadedLogs = Array.isArray(data?.logsSend) ? data.logsSend : [];
  this.logFileNames = this.buildLogFileCandidates(data?.logFileNames, this.templateFile);

       this.appendLogLines(this.preloadedLogs);
       this.startPollingLogs();
       
  }
  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.scrollLogsToBottom();
  }

  ngOnDestroy(): void {
    this.isClosed = true;
    this.clearPendingPoll();
  }

  apiCalllImport(){
    
    const toyotaGUID = localStorage.getItem('toyotaGUID');
    this.importService.getImportLogs(toyotaGUID,this.templateFile)?.subscribe(
      (response: any) => {
        this.displaydata = JSON.parse(response.body);
        
      }, _error => {
        this.errormsg = "Something Went Wrong"
      });
  }

  apiCalllValidate(){
    
    const toyotaGUID = localStorage.getItem('toyotaGUID');
    this.importService.getUploadLogs(toyotaGUID,this.templateFile)?.subscribe(
      (response: any) => {
        this.displaydata = JSON.parse(response.body);
        
      }, _error => {
        this.errormsg = "Something Went Wrong"
      });
  }

  private fetchLogs(isValidationFlow: boolean): void {
    if (this.isClosed || this.hasTerminalState) {
      return;
    }

    if (this.hasTerminalLineInDisplayData()) {
      this.hasTerminalState = true;
      this.clearPendingPoll();
      return;
    }

    const toyotaGUID = localStorage.getItem('toyotaGUID');
    const lookupFile = this.resolveLookupFileCandidate();
    const request$ = isValidationFlow
      ? this.importService.getUploadLogs(toyotaGUID, lookupFile)
      : this.importService.getImportLogs(toyotaGUID, lookupFile);

    request$?.subscribe({
      next: (response: any) => {
        const logs = this.normalizeLogs(response);
        const lines = logs.map((entry) => entry.message);
        this.appendLogLines(lines);

        if (this.hasTerminalState || this.hasTerminalLineInDisplayData()) {
          this.hasTerminalState = true;
          this.clearPendingPoll();
          return;
        }

        if (this.containsTerminalMarker(lines)) {
          this.hasTerminalState = true;
          this.clearPendingPoll();
          return;
        }

        this.scheduleNextPoll(isValidationFlow);
      },
      error: () => {
        this.scheduleNextPoll(isValidationFlow);
      }
    });
  }

  private startPollingLogs(): void {
    if(this.value == 'validate') { // NOSONAR
      this.fetchLogs(true);
    } else {
      this.fetchLogs(false);
    }
  }

  private scheduleNextPoll(isValidationFlow: boolean): void {
    if (this.isClosed || this.hasTerminalState || this.hasTerminalLineInDisplayData()) {
      this.hasTerminalState = true;
      this.clearPendingPoll();
      return;
    }

    this.pollAttempt += 1;
    if (this.pollAttempt >= this.maxPollAttempts) {
      if (this.displaydata.length === 0 && !this.errormsg) {
        this.errormsg = 'No logs available.';
      }
      return;
    }

    this.clearPendingPoll();
    this.pollTimeoutRef = setTimeout(() => this.fetchLogs(isValidationFlow), this.retryDelayMs);
  }

  public appendLogLine(line: string): void {
    this.appendLogLines([line]);
  }

  public appendLogLines(lines: string[]): void {
    const normalizedLines = (lines || [])
      .map((line) => String(line || '').trim())
      .filter(Boolean);

    if (normalizedLines.length === 0) {
      return;
    }

    normalizedLines.forEach((line) => {
      if (!this.lineSet.has(line)) {
        this.lineSet.add(line);
        this.displaydata.push({ message: line });
      }
    });

    if (this.containsTerminalMarker(normalizedLines)) {
      this.hasTerminalState = true;
      this.clearPendingPoll();
    }

    this.errormsg = undefined;
    this.scrollLogsToBottom();
  }

  private scrollLogsToBottom(): void {
    setTimeout(() => {
      const container = this.logsContainer?.nativeElement;
      if (!container) {
        return;
      }

      container.scrollTop = container.scrollHeight;
    });
  }

  private normalizeLogs(response: any): Array<{ message: string }> {
    const payload = this.parseBody(response?.body ?? response);
    const messages = this.collectLogMessages(payload);
    return messages.map((message) => ({ message }));
  }

  private collectLogMessages(source: any, seen = new Set<any>()): string[] {
    if (source === null || source === undefined) {
      return [];
    }

    if (typeof source === 'string') {
      const parsed = this.parseBody(source);
      if (parsed !== source) {
        return this.collectLogMessages(parsed, seen);
      }

      return source
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    }

    if (typeof source !== 'object') {
      return [];
    }

    if (seen.has(source)) {
      return [];
    }
    seen.add(source);

    if (Array.isArray(source)) {
      return source.flatMap((item) => this.collectLogMessages(item, seen));
    }

    const directMessageCandidates = [
      source.message,
      source.logMessage,
      source.msg,
      source.errorMessage,
      source.errorMsg,
    ]
      .filter((value) => typeof value === 'string')
      .map((value) => String(value).trim())
      .filter(Boolean);

    const nestedCandidates = [
      source.body,
      source.logs,
      source.log,
      source.data,
      source.result,
      source.response,
      source.messages,
      source.errorMessages,
      source.uploadLogs,
      source.importLogs,
      source.validationLogs,
      source.details,
      source.payload,
    ];

    const nestedMessages = nestedCandidates.flatMap((item) => this.collectLogMessages(item, seen));

    if (directMessageCandidates.length > 0 || nestedMessages.length > 0) {
      return [...directMessageCandidates, ...nestedMessages]
        .map((line) => line.trim())
        .filter(Boolean);
    }

    // Fallback 1: pull string fields that look like log lines.
    const heuristicMessages = Object.entries(source)
      .filter(([key, value]) => typeof value === 'string' && /(log|message|error|status)/i.test(key))
      .map(([, value]) => String(value).trim())
      .filter(Boolean);

    if (heuristicMessages.length > 0) {
      return heuristicMessages;
    }

    // Fallback 2: recursively scan all object values (handles numeric-key maps and custom payload shapes).
    return Object.values(source)
      .flatMap((value) => this.collectLogMessages(value, seen))
      .map((line) => String(line || '').trim())
      .filter(Boolean);
  }

  private parseBody(body: any): any {
    if (typeof body !== 'string') {
      return body;
    }

    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }

  private buildLogFileCandidates(rawCandidates: any, fallbackFile: any): string[] {
    const candidates = Array.isArray(rawCandidates) ? rawCandidates : [];
    const fallbackName = fallbackFile?.name || fallbackFile?.fileName || '';
    const merged = [...candidates, fallbackName]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    return Array.from(new Set(merged));
  }

  private resolveLookupFileCandidate(): File | string {
    if (!this.logFileNames.length) {
      return this.templateFile;
    }

    const candidate = this.logFileNames[this.logFileNameCursor % this.logFileNames.length];
    this.logFileNameCursor += 1;
    return candidate;
  }

  private containsTerminalMarker(lines: string[]): boolean {
    return (lines || []).some((line) => {
      const normalized = String(line || '').toLowerCase();
      return this.terminalMarkers.some((marker) => normalized.includes(marker));
    });
  }

  private hasTerminalLineInDisplayData(): boolean {
    const lines = (this.displaydata || []).map((item: any) => String(item?.message || '').trim());
    return this.containsTerminalMarker(lines);
  }

  private clearPendingPoll(): void {
    if (this.pollTimeoutRef) {
      clearTimeout(this.pollTimeoutRef);
      this.pollTimeoutRef = undefined;
    }
  }

  closeDialog(){
    this.isClosed = true;
    this.clearPendingPoll();
    this.dialogRef.close();
  }

}
